import { isFinanceProject, isGlobalProject } from 'config';
import { applySchema } from 'helpers';
import {
  TypedForm,
  FormErrors as FormErrorsBase,
  ObjectSchema,
  object,
} from 'libs/form';
import { TFunc } from 'libs/i18n';
import {
  AdditionalDocumentsFormType,
  getAdditionalDocumentsSchema,
} from 'modules/client/common/forms/additionalDocumentsForm';
import {
  DocumentsFormType,
  documentsSchema,
} from 'modules/client/common/forms/documentsForm';
import {
  ReasonChangeFormType,
  reasonChangeSchema,
} from 'modules/client/common/forms/reasonChangeForm';
import {
  CompanyConstituentDocsDateFormType,
  getCompanyConstituentDocsDateSchema,
} from 'modules/client/legal/forms/companyConstituentDocsDate';
import {
  CompanyConstituentDocsFilesFormType,
  getCompanyConstituentDocsFilesSchema,
} from 'modules/client/legal/forms/companyConstituentDocsFiles';

import { ActivityFormType, activitySchema } from './activityForm';
import { BiographyFormType, biographySchema } from './biographyForm';
import { CitizenshipFormType, citizenshipsSchema } from './citizenshipForm';
import { CompanyNameFormType, companyNameSchema } from './companyNameForm';
import {
  CompanyRegistrationAddressFormType,
  companyRegistrationAddressSchema,
} from './companyRegistrationAddressForm';
import {
  CompanyResidentialAddressFormType,
  companyResidentialAddressSchema,
} from './companyResidentialAddressForm';
import {
  CompanyStateRegistrationFormType,
  getCompanyStateRegistrationSchema,
} from './companyStateRegistrationForm';
import {
  CompanyTaxResidencyFormType,
  companyTaxResidencySchema,
} from './companyTaxResidencyForm';
import { ContactsFormType, getContactsSchema } from './contactsForm';
import {
  IdentityDocumentFormType,
  identityDocumentSchema,
} from './identityDocumentForm';
import {
  PepConnectionFormType,
  getPepConnectionSchema,
} from './pepConnectionForm';
import {
  PersonalDataFormType,
  getPersonalDataSchema,
} from './personalDataForm';
import { PlaceOfBirthFormType, placeOfBirthSchema } from './placeOfBirthForm';
import {
  RegistrationAddressFormType,
  registrationAddressSchema,
} from './registrationAddressForm';
import {
  ResidentialAddressFormType,
  residentialAddressWithFilesSchema,
} from './residentialAddressForm';
import {
  RoleInCompanyFormType,
  getRoleInCompanySchema,
} from './roleInCompanyForm';
import {
  TaxResidencyFormType,
  getTaxResidencesSchema,
} from './taxResidencyForm';

export interface LegalRepresentativeFormType
  extends ActivityFormType,
    PersonalDataFormType,
    RegistrationAddressFormType,
    ResidentialAddressFormType,
    CitizenshipFormType,
    PlaceOfBirthFormType,
    BiographyFormType,
    TaxResidencyFormType,
    PepConnectionFormType,
    ContactsFormType,
    RoleInCompanyFormType,
    CompanyNameFormType,
    CompanyRegistrationAddressFormType,
    CompanyResidentialAddressFormType,
    CompanyTaxResidencyFormType,
    CompanyStateRegistrationFormType,
    IdentityDocumentFormType,
    AdditionalDocumentsFormType,
    DocumentsFormType,
    ReasonChangeFormType,
    CompanyConstituentDocsFilesFormType,
    CompanyConstituentDocsDateFormType {}

export const getSchema = (
  t: TFunc,
  isCompany: boolean
): ObjectSchema<LegalRepresentativeFormType> => {
  const isGlobal = isGlobalProject();
  const isFinance = isFinanceProject();
  return object()
    .concat(activitySchema)
    .concat(applySchema(isFinance, registrationAddressSchema))
    .concat(residentialAddressWithFilesSchema)
    .concat(placeOfBirthSchema)
    .concat(citizenshipsSchema)
    .concat(getTaxResidencesSchema(isGlobal))
    .concat(applySchema(isFinance, biographySchema))
    .concat(applySchema(isFinance && isCompany, companyNameSchema))
    .concat(
      applySchema(isFinance && isCompany, companyRegistrationAddressSchema)
    )
    .concat(
      applySchema(isFinance && isCompany, companyResidentialAddressSchema)
    )
    .concat(applySchema(isFinance && isCompany, companyTaxResidencySchema))
    .concat(identityDocumentSchema)
    .concat(getRoleInCompanySchema(t, { enabledCivilLawSubject: isFinance }))
    .concat(getContactsSchema(t))
    .concat(getPepConnectionSchema(t, { notRequestInformation: isGlobal }))
    .concat(getPersonalDataSchema(t, { enabledGender: isGlobal }))
    .concat(getCompanyStateRegistrationSchema(t))
    .concat(getAdditionalDocumentsSchema())
    .concat(reasonChangeSchema)
    .concat(documentsSchema)
    .concat(
      applySchema(
        isFinance && isCompany,
        getCompanyConstituentDocsFilesSchema()
      )
    )
    .concat(
      applySchema(
        isFinance && isCompany,
        getCompanyConstituentDocsDateSchema(t)
      )
    );
};

export const { Field, Form, useFormContext, Submit } =
  TypedForm<LegalRepresentativeFormType>();

export type FormErrors = FormErrorsBase<LegalRepresentativeFormType>;
